import { DefaultRootState } from "stores";
import { DocumentReference, Query, onSnapshot } from "firebase/firestore";
import { DefaultThunk } from "stores";
import { createEntitySlice, createEntitySliceGroupBy } from "./slice";

type QueryCreator<T, U> = (
  props: T,
  state: DefaultRootState
) => Query<U> | null;

export const createSubscribeCollection =
  <T, P>(
    actions:
      | ReturnType<typeof createEntitySliceGroupBy<T>>["actions"]
      | ReturnType<typeof createEntitySlice<T>>["actions"],
    queryCreator: QueryCreator<P, T>,
    reversed: boolean = false
  ) =>
  (props: P): DefaultThunk<() => void> =>
  (dispatch, getState) => {
    const query = queryCreator(props, getState());
    if (!query) return () => {};

    const unsubscribe = onSnapshot(query, (snapshot) => {
      const changes = snapshot.docChanges();
      if (reversed) {
        changes.reverse();

        // reversed が true の場合は個別にディスパッチ
        for (const { type, doc } of changes) {
          switch (type) {
            case "added": {
              dispatch(
                actions.add({
                  id: doc.id,
                  entity: {
                    _id: doc.id,
                    ...doc.data({ serverTimestamps: "previous" }),
                  } as T,
                })
              );
              break;
            }
            case "modified": {
              dispatch(
                actions.update({
                  id: doc.id,
                  entity: {
                    _id: doc.id,
                    ...doc.data({ serverTimestamps: "previous" }),
                  } as T,
                })
              );
              break;
            }
            case "removed": {
              dispatch(actions.remove(doc.id));
              break;
            }
          }
        }
      } else {
        // 変更をタイプ別に分類（バッチ処理）
        const added: { id: string; entity: T }[] = [];
        const updated: { id: string; entity: T }[] = [];
        const removed: string[] = [];

        for (const { type, doc } of changes) {
          switch (type) {
            case "added": {
              added.push({
                id: doc.id,
                entity: {
                  _id: doc.id,
                  ...doc.data({ serverTimestamps: "previous" }),
                } as T,
              });
              break;
            }
            case "modified": {
              updated.push({
                id: doc.id,
                entity: {
                  _id: doc.id,
                  ...doc.data({ serverTimestamps: "previous" }),
                } as T,
              });
              break;
            }
            case "removed": {
              removed.push(doc.id);
              break;
            }
          }
        }

        // バッチアクションをディスパッチ
        if (added.length > 0) {
          dispatch(actions.addMany(added));
        }
        if (updated.length > 0) {
          dispatch(actions.updateMany(updated));
        }
        if (removed.length > 0) {
          dispatch(actions.removeMany(removed));
        }
      }
    });

    return () => {
      unsubscribe();
      dispatch(actions.init());
    };
  };

type DocSelector<T> = (props: T) => DocumentReference | null;

export const createSubscribeDocument =
  <T, P>(
    actions:
      | ReturnType<typeof createEntitySliceGroupBy<T>>["actions"]
      | ReturnType<typeof createEntitySlice<T>>["actions"],
    docSelector: DocSelector<P>
  ) =>
  (selectorProps: P, onNotfound?: () => void): DefaultThunk<() => void> =>
  (dispatch) => {
    const query = docSelector(selectorProps);
    if (!query) return () => {};

    return onSnapshot(query, (doc) => {
      const data = doc.data({ serverTimestamps: "previous" });

      if (data === undefined) {
        if (onNotfound) {
          onNotfound();
        }
        return;
      }

      dispatch(
        actions.update({
          id: doc.id,
          entity: {
            _id: doc.id,
            ...(data as T),
          },
        })
      );
    });
  };
